import api from './api';

/**
 * The agent's OWN data — `/api/v1/posp-portal/`.
 *
 * Kept out of `services/brokers.ts` on purpose: everything in there is the
 * brokerage's view of its agents, and these two resources are explicitly not
 * the brokerage's. Synced contacts in particular are private to the agent and
 * have no broker-side route at all, so there is nothing for a broker page to
 * import from here.
 */

export interface SyncedContact {
  id: string;
  name: string;
  /** As the device reported it. Read-only: the number is half the row's identity. */
  phone: string;
  email: string;
  notes: string;
  device_contact_id: string;
  first_synced_at: string;
  last_synced_at: string;
  created_at: string;
  updated_at: string;
}

export interface SyncedContactPage {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: SyncedContact[];
}

/** What the phone sends up. Everything but `phone` is optional — an address
 *  book is full of entries that are only a number. */
export interface DeviceContact {
  name?: string;
  phone: string;
  email?: string;
  device_contact_id?: string;
}

/** Counts only. The server deliberately never echoes the contacts back. */
export interface SyncResult {
  received: number;
  created: number;
  updated: number;
  skipped: number;
  total: number;
}

/** Mirrors `MAX_CONTACTS_PER_SYNC` on the server, which refuses a bigger batch. */
export const MAX_CONTACTS_PER_SYNC = 500;

export const pospContactsApi = {
  list: async (params: { page?: number; page_size?: number; search?: string } = {}) => {
    const { data } = await api.get<SyncedContactPage>('/posp-portal/contacts/', { params });
    return data;
  },
  sync: async (contacts: DeviceContact[]) => {
    const { data } = await api.post<SyncResult>('/posp-portal/contacts/sync/', { contacts });
    return data;
  },
  update: async (id: string, patch: Partial<Pick<SyncedContact, 'name' | 'email' | 'notes'>>) => {
    const { data } = await api.patch<SyncedContact>(`/posp-portal/contacts/${id}/`, patch);
    return data;
  },
  remove: async (id: string) => {
    await api.delete(`/posp-portal/contacts/${id}/`);
  },
};

export interface BrokerMappingRequest {
  id: string;
  posp: string;
  posp_code: string;
  posp_name: string;
  to_broker: string;
  to_broker_name: string;
  from_broker: string | null;
  from_broker_name: string;
  status: 'pending' | 'approved' | 'declined' | 'withdrawn';
  note: string;
  decline_reason: string;
  /** True only when approval actually re-pointed the agent. */
  applied: boolean;
  /** When approved but not applied, exactly what business blocked the move. */
  blocked_reason: string;
  reviewed_at: string | null;
  created_at: string;
}

export interface BrokerChoice {
  id: string;
  name: string;
  code: string;
}

export const brokerMappingRequestsApi = {
  /** Brokerages the agent may ask to join. Served by the same viewset rather
   *  than `/brokers/`, which an agent has no access to. */
  brokers: async () => {
    const { data } = await api.get<{ results: BrokerChoice[] }>(
      '/posp-portal/broker-mapping-requests/brokers/'
    );
    return data.results;
  },
  /** `status` narrows to one value ('pending', 'approved', ...); omitted or
   *  'all' returns everything the viewer's role can see. The agent's own card
   *  calls this with no filter (it wants its whole history); the receiving
   *  broker's queue asks for `status: 'pending'` and later re-reads with 'all'
   *  to confirm a verdict actually landed. */
  list: async (params: { status?: string } = {}) => {
    const { data } = await api.get<{ count: number; results: BrokerMappingRequest[] }>(
      '/posp-portal/broker-mapping-requests/',
      { params }
    );
    return data;
  },
  create: async (toBroker: string, note: string) => {
    const { data } = await api.post<BrokerMappingRequest>('/posp-portal/broker-mapping-requests/', {
      to_broker: toBroker,
      note,
    });
    return data;
  },
  withdraw: async (id: string) => {
    const { data } = await api.post<BrokerMappingRequest>(
      `/posp-portal/broker-mapping-requests/${id}/withdraw/`
    );
    return data;
  },
  /** Only the receiving broker's admin (or a country admin) may call this --
   *  the API 403s anyone else. Consequential: see `PospBrokerMappingRequest`
   *  and `BrokerMappingRequestViewSet._apply_transfer` on the backend for
   *  exactly what moves. The response's `applied`/`blocked_reason` say whether
   *  the agent was actually re-pointed or the approval only recorded consent
   *  -- callers must render that field, never assume approved == moved. */
  approve: async (id: string) => {
    const { data } = await api.post<BrokerMappingRequest>(
      `/posp-portal/broker-mapping-requests/${id}/approve/`
    );
    return data;
  },
  /** `reason` is not enforced non-empty by the API itself (it stores whatever
   *  is sent, including ''), so the UI is what makes a reason mandatory. It
   *  lands in `decline_reason` and is shown back to the agent on their own
   *  request card. */
  decline: async (id: string, reason: string) => {
    const { data } = await api.post<BrokerMappingRequest>(
      `/posp-portal/broker-mapping-requests/${id}/decline/`,
      { reason }
    );
    return data;
  },
};
